import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import App from './App';
import { AuthProvider, activeAdminTenantId, useAuth } from './auth/AuthContext';
import { ImpersonationProvider } from './auth/ImpersonationContext';
import { ThemeModeProvider } from './theme/ThemeModeContext';
import { SelectedTenantProvider } from './components/SelectedTenantContext';
import { StagingOffHoursNotice } from './components/StagingOffHoursNotice';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import { NewVersionBanner } from './components/NewVersionBanner';
import { DebriefRecorderProvider } from './components/debrief/DebriefRecorder';
import { DisplayTimeZoneProvider } from './util/displayTimeZone';

// Feeds the active user's tenant time-display setting (TIMEZONE-DISPLAY) to the
// whole app, so `useFormatDateTime` renders in the tenant's zone. Sits inside
// AuthProvider (it reads the user) and updates automatically on login and when
// an act-as session changes who the current user is.
function DisplayTimeZoneFromAuth({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const timeZone = React.useMemo(() => {
    if (!user) return undefined;
    // Prefer the ACTIVE tenant's zone (activeAdminTenantId honours the
    // multi-tenant staff pin), so a tenant's data renders in ITS zone whoever
    // is viewing — a superadmin or multi-tenant admin included.
    const adminTid = activeAdminTenantId(user);
    if (adminTid) {
      const m = user.memberships.find((x) => x.tenant_id === adminTid);
      if (m?.display_timezone) return m.display_timezone;
    }
    // Else the server-derived single-tenant value (covers participants).
    return user.display_timezone ?? undefined;
  }, [user]);
  return (
    <DisplayTimeZoneProvider timeZone={timeZone}>
      {children}
    </DisplayTimeZoneProvider>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

// Provider order matters: ThemeModeProvider supplies the MUI theme +
// CssBaseline that downstream UI uses; AuthProvider needs the React
// Router context to be available when it triggers a redirect.
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeModeProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <DisplayTimeZoneFromAuth>
            <ImpersonationProvider>
              <SelectedTenantProvider>
                <DebriefRecorderProvider>
                <App />
                {/* Global: catches API transport failures and, on staging
                    outside business hours, explains the box is asleep. */}
                <StagingOffHoursNotice />
                {/* Global: the act-as banner + viewport frame, shown on
                    every route while a support session is active. */}
                <ImpersonationBanner />
                {/* Global: offers a reload once a newer build is deployed. */}
                <NewVersionBanner />
                </DebriefRecorderProvider>
              </SelectedTenantProvider>
            </ImpersonationProvider>
            </DisplayTimeZoneFromAuth>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeModeProvider>
  </React.StrictMode>,
);
